import type { ChangeEvent, KeyboardEvent, RefObject } from 'react'
import { useEffect, useRef, useState } from 'react'

export type InlineEditElement = HTMLInputElement | HTMLTextAreaElement

export interface UseInlineEditOptions {
  // Сохранённое значение — источник правды: с него стартует черновик, к нему
  // возвращает отмена.
  value: string
  onCommit: (next: string) => void
  onCancel?: (() => void) | undefined
  // Многострочный ввод: Enter переносит строку, коммит — Ctrl/Cmd+Enter.
  multiline?: boolean | undefined
  // Пустое значение осмысленно (описание можно стереть) — иначе оно трактуется
  // как отмена и мутация не уходит.
  allowEmpty?: boolean | undefined
  selectOnStart?: boolean | undefined
}

export interface InlineEditProps<E extends InlineEditElement> {
  ref: RefObject<E | null>
  value: string
  onChange: (e: ChangeEvent<E>) => void
  onBlur: () => void
  onKeyDown: (e: KeyboardEvent<E>) => void
}

export interface InlineEdit<E extends InlineEditElement> {
  editing: boolean
  draft: string
  setDraft: (next: string) => void
  start: () => void
  cancel: () => void
  commit: () => void
  ref: RefObject<E | null>
  handleBlur: () => void
  handleKeyDown: (e: KeyboardEvent<E>) => void
  // Готовый набор для <input>/<textarea>; контролы со своим API (markdown-редактор)
  // берут draft/setDraft/handle* по отдельности.
  inputProps: InlineEditProps<E>
}

// Петля правки по месту: start() → правка → Enter (или Ctrl/Cmd+Enter в multiline)
// коммитит, Escape отменяет, клик мимо коммитит.
export function useInlineEdit<E extends InlineEditElement = HTMLInputElement>({
  value,
  onCommit,
  onCancel,
  multiline = false,
  allowEmpty = false,
  selectOnStart = true,
}: UseInlineEditOptions): InlineEdit<E> {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const ref = useRef<E>(null)
  const awaitingWindow = useRef(false)

  // Фокус в поле сразу после его появления (без autoFocus — a11y-линт): иначе
  // печатать нечем, а Enter/Escape не долетают.
  useEffect(() => {
    if (!editing) return
    const el = ref.current
    el?.focus()
    if (selectOnStart) el?.select()
  }, [editing, selectOnStart])

  // Окно вернуло фокус — возвращаем его в поле, где правка так и осталась открытой.
  useEffect(() => {
    if (!editing) return
    const onWindowFocus = () => {
      if (!awaitingWindow.current) return
      awaitingWindow.current = false
      ref.current?.focus()
    }
    window.addEventListener('focus', onWindowFocus)
    return () => window.removeEventListener('focus', onWindowFocus)
  }, [editing])

  const start = () => {
    setDraft(value)
    setEditing(true)
  }

  const cancel = () => {
    awaitingWindow.current = false
    setDraft(value)
    setEditing(false)
    onCancel?.()
  }

  const commit = () => {
    awaitingWindow.current = false
    const next = draft.trim()
    setEditing(false)
    if ((!next && !allowEmpty) || next === value) {
      setDraft(value)
      return
    }
    onCommit(next)
  }

  const handleBlur = () => {
    // Фокус потеряло само окно (смена раскладки, переключение окна) — правку
    // держим открытой: коммит здесь стирал бы набранное на ровном месте.
    if (!document.hasFocus()) {
      awaitingWindow.current = true
      return
    }
    commit()
  }

  const handleKeyDown = (e: KeyboardEvent<E>) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      cancel()
      return
    }
    if (e.key !== 'Enter') return
    if (multiline && !e.metaKey && !e.ctrlKey) return
    e.preventDefault()
    commit()
  }

  return {
    editing,
    draft,
    setDraft,
    start,
    cancel,
    commit,
    ref,
    handleBlur,
    handleKeyDown,
    inputProps: {
      ref,
      value: draft,
      onChange: (e: ChangeEvent<E>) => setDraft(e.target.value),
      onBlur: handleBlur,
      onKeyDown: handleKeyDown,
    },
  }
}
